<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @Jorel254 - Versión inicial
 */

import { computed, ref, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
/**
 * @description Props del componente
 */

const error = ref<string | null>(null)
const submitting = ref(false)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const excelFile = ref<File | undefined>(undefined)
const processingExcel = ref(false)
const convertToUpperCase = ref(true)
const store = useStore()
const logo128 = ref<File | undefined>(undefined)
const inAppSign = ref(false)
const CCT = ref('')
const hasCertisep = ref(false)

let institutionData = ref<any | null>(null)
const creditosExcelBlob = ref<Blob | null>(null)
const selectedInstitution = computed(() => store.state.global.selectedInstitution)
/**
 * @description Procesa el archivo Excel cuando se selecciona
 */
const handleExcelFileChange = async () => {
	if (!excelFile.value) {
		return
	}

	const file = excelFile.value
	if (!file) {
		return
	}

	processingExcel.value = true
	error.value = null

	try {
		const reader = new FileReader()

		reader.onload = async e => {
			try {
				const arrayBuffer = e.target?.result as ArrayBuffer
				// Convertir ArrayBuffer a base64 de manera eficiente
				const uint8Array = new Uint8Array(arrayBuffer)
				let binary = ''
				for (const element of uint8Array) {
					binary += String.fromCodePoint(element)
				}
				const base64 = btoa(binary)

				const result = await apiService.post<{
					code: number
					message: string
					data: {
						json: any
						creditosExcelBase64: string
					}
				}>('processInstitutionExcel', {
					excelBase64: base64,
					convertMayus: convertToUpperCase.value
				})
				if (result.code === 1 && result.data) {
					institutionData.value = result.data.json

					// Convertir base64 a Blob para la descarga
					const byteCharacters = atob(result.data.creditosExcelBase64)
					// oxlint-disable-next-line no-new-array
					const byteNumbers = new Array(byteCharacters.length)
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.codePointAt(i)
					}
					const byteArray = new Uint8Array(byteNumbers)
					creditosExcelBlob.value = new Blob([byteArray], {
						type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
					})

					// Descargar automáticamente el Excel de créditos
					if (creditosExcelBlob.value) {
						const link = document.createElement('a')
						link.href = globalThis.URL.createObjectURL(creditosExcelBlob.value)
						link.download = 'creditosTipoAsignatura.xlsx'
						document.body.appendChild(link)
						link.click()
						link.remove()
						globalThis.URL.revokeObjectURL(link.href)
					}

					successMessage.value = 'Excel procesado correctamente. Los datos de la institución han sido cargados y el archivo creditosTipoAsignatura.xlsx ha sido descargado.'
					showSuccessModal.value = true
				} else {
					throw new Error(result.message || 'Error al procesar el archivo Excel')
				}
			} catch (err: any) {
				console.error('Error al procesar el Excel:', err)
				error.value = err.message || 'Error al procesar el archivo Excel'
			} finally {
				processingExcel.value = false
			}
		}

		reader.onerror = () => {
			error.value = 'Error al leer el archivo Excel'
			processingExcel.value = false
		}

		reader.readAsArrayBuffer(file)
	} catch (err: any) {
		console.error('Error al procesar el Excel:', err)
		error.value = err.message || 'Error al procesar el archivo Excel'
		processingExcel.value = false
	}
}

// Watcher para procesar el archivo cuando se seleccione
watch(
	excelFile,
	newFiles => {
		if (newFiles) {
			handleExcelFileChange()
		} else {
			institutionData.value = null
		}
	},
	{ deep: true }
)
watch(
	logo128,
	newFile => {
		if (newFile) {
			handleLogo128Change()
		} else if (institutionData.value) {
			const updatedData = { ...institutionData.value }
			delete updatedData.logo128
			institutionData.value = updatedData
		}
	},
	{ deep: true }
)

watch(inAppSign, newVal => {
	if (institutionData.value && newVal !== undefined) {
		institutionData.value = {
			...institutionData.value,
			certisep: {
				inAppSign: newVal
			}
		}
	} else {
		institutionData.value = {
			idNombreInstitucion: store.state.global.selectedInstitution.idNombreInstitucion,
			certisep: {
				inAppSign: newVal
			}
		}
	}
})
watch(hasCertisep, newVal => {
	if (institutionData.value && newVal !== undefined) {
		institutionData.value = {
			...institutionData.value,
			hasCertisep: newVal
		}
	} else {
		institutionData.value = {
			idNombreInstitucion: store.state.global.selectedInstitution.idNombreInstitucion,
			hasCertisep: newVal
		}
	}
})
watch(CCT, newVal => {
	if (institutionData.value && newVal !== undefined) {
		institutionData.value = {
			...institutionData.value,
			CCT: newVal
		}
	} else {
		institutionData.value = {
			idNombreInstitucion: store.state.global.selectedInstitution.idNombreInstitucion,
			CCT: newVal
		}
	}
})
/**
 * @description Convierte el logo a base64 redimensionándolo a 128x128 píxeles
 * Usa un canvas para redimensionar la imagen antes de convertirla a base64
 */
const handleLogo128Change = async () => {
	try {
		const raw = logo128.value as File | File[] | undefined
		const file = Array.isArray(raw) ? raw[0] : raw
		if (!file) {
			return
		}

		// Verificar que sea una imagen
		if (!file.type?.startsWith('image/')) {
			throw new Error('El archivo debe ser una imagen')
		}

		// Crear una promesa para cargar y redimensionar la imagen
		const base64String = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = (e: ProgressEvent<FileReader>) => {
				const img = new Image()
				img.onload = () => {
					// Crear un canvas de 128x128
					const canvas = document.createElement('canvas')
					canvas.width = 128
					canvas.height = 128
					const ctx = canvas.getContext('2d')

					if (!ctx) {
						reject(new Error('No se pudo obtener el contexto del canvas'))
						return
					}

					// Dibujar la imagen redimensionada en el canvas
					ctx.drawImage(img, 0, 0, 128, 128)

					// Convertir el canvas a base64
					// Usar toDataURL con el tipo de imagen original o PNG por defecto
					const mimeType = file.type || 'image/png'
					const dataUrl = canvas.toDataURL(mimeType, 0.9)

					// Extraer solo la parte base64 (sin el prefijo data:image/...;base64,)
					const base64 = dataUrl.split(',')[1]
					resolve(base64)
				}
				img.onerror = () => {
					reject(new Error('Error al cargar la imagen'))
				}

				if (e.target?.result) {
					img.src = e.target.result as string
				} else {
					reject(new Error('No se pudo leer el archivo'))
				}
			}
			reader.onerror = () => {
				reject(new Error('Error al leer el archivo'))
			}
			reader.readAsDataURL(file)
		})
		if (institutionData.value) {
			institutionData.value = {
				...institutionData.value,
				logo128: base64String
			}
		} else {
			institutionData.value = {
				idNombreInstitucion: store.state.global.selectedInstitution.idNombreInstitucion,
				logo128: base64String
			}
		}
	} catch (err: any) {
		console.error('Error al procesar el logo 128x128:', err)
		error.value = err.message || 'Error al procesar el logo'
		showErrorModal.value = true
	}
}
const copyToClipboard = (data: any) => {
	navigator.clipboard.writeText(JSON.stringify(data, null, 2))
	successMessage.value = 'Datos copiados al portapapeles'
	showSuccessModal.value = true
}

/**
 * @description Formatea JSON con colores para visualización mejorada
 */
const formatJsonWithColors = (data: any): string => {
	if (!data) {
		return '<span class="json-null">No hay datos de la institución</span>'
	}

	const jsonString = JSON.stringify(data, null, 2)

	// Escapar HTML para seguridad
	const escapeHtml = (text: string) => {
		const map: Record<string, string> = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#039;'
		}
		return text.replace(/[&<>"']/g, m => map[m])
	}

	// Aplicar colores usando regex (procesar en orden específico)
	let formatted = escapeHtml(jsonString)

	// Colorear strings primero (para evitar conflictos con otros elementos)
	formatted = formatted.replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, '<span class="json-string">"$1"</span>')

	formatted = formatted.replace(/\bnull\b(?!<span[^>]{0,50}>)/g, '<span class="json-null">null</span>')
	formatted = formatted.replace(/\b(true|false)\b(?!<span[^>]{0,50}>)/g, '<span class="json-boolean">$1</span>')
	formatted = formatted.replace(/\b(-?\d+\.?\d*)\b(?!<span[^>]{0,50}>)/g, '<span class="json-number">$1</span>')

	// Colorear llaves, corchetes, dos puntos y comas (solo fuera de tags span)
	formatted = formatted.replace(/(\{|\})(?!<span[^>]{0,50}>)/g, '<span class="json-brace">$1</span>')
	formatted = formatted.replace(/(\[|\])(?!<span[^>]{0,50}>)/g, '<span class="json-bracket">$1</span>')
	formatted = formatted.replace(/:(?!<span[^>]{0,50}>)/g, '<span class="json-colon">:</span>')
	formatted = formatted.replace(/,(?!<span[^>]{0,50}>)/g, '<span class="json-comma">,</span>')

	return formatted
}

const saveNewChanges = async () => {
	try {
		submitting.value = true
		error.value = null

		// Obtener el ID de la institución actual
		const institutionId = store.getters['global/currentInstitution']
		if (!institutionId) {
			throw new Error('No hay una institución seleccionada')
		}

		// Validar que hay datos nuevos para actualizar
		if (!institutionData.value) {
			throw new Error('No hay datos de institución para actualizar. Por favor, carga un archivo Excel primero.')
		}
		institutionData.value = {
			...institutionData.value,
			nombreInstitucion: store.state.global.selectedInstitution.nombreInstitucion
		}
		if (institutionData.value.idNombreInstitucion !== store.state.global.selectedInstitution.idNombreInstitucion) {
			error.value = `El ID de nombre de la institución no coincide con el ID de nombre de la institución que queremos actualizar.`
			showErrorModal.value = true
			return
		}
		// Actualizar usando el servicio
		let fieldsUpdated = 0
		try {
			const response = await apiService.post<{ code: number; message: string; data?: { fieldsUpdated: number } }>('updateInstitutionData', {
				institutionId: Number(institutionId),
				newData: institutionData.value
			})

			if (response.code === -1) {
				throw new Error(response.message || 'Error al actualizar la institución')
			}

			fieldsUpdated = response.data?.fieldsUpdated ?? 0
		} catch (error) {
			console.error('Error al actualizar la institución:', error)
			throw new Error(`Error al actualizar los datos de la institución: ${(error as Error).message}`)
		}

		if (fieldsUpdated === 0) {
			successMessage.value = 'No hay campos nuevos para actualizar. Todos los datos ya existen en la institución.'
			showSuccessModal.value = true
		} else {
			successMessage.value = `Institución actualizada exitosamente. Se agregaron ${fieldsUpdated} campo(s) nuevo(s).`
			showSuccessModal.value = true
		}
		setTimeout(() => {
			globalThis.location.reload()
		}, 6000)
	} catch (err: any) {
		console.error('Error al actualizar la institución:', err)
		error.value = err.message || 'Error al actualizar los datos de la institución'
		showErrorModal.value = true
	} finally {
		submitting.value = false
	}
}

function tieneValor<T>(value: T | null | undefined | '' | [] | {}): value is T {
	if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
		return false
	}
	if (typeof value === 'string') return value.trim().length > 0
	if (typeof value === 'object') return Object.keys(value).length > 0
	return true
}

const statusFields = [
	{ key: 'Carrera', label: 'Carrera' },
	{ key: 'certisep', label: 'Firma app' },
	{ key: 'Campus', label: 'Campus' },
	{ key: 'idNombreInstitucion', label: 'ID Nombre Institución' },
	{ key: 'cveInstitution', label: 'Clave Institución' },
	{ key: 'Responsable', label: 'Responsables' },
	{ key: 'idCampus', label: 'ID Campus' },
	{ key: 'nombreInstitucion', label: 'Nombre Institución' },
	{ key: 'logo128', label: 'Logo' },
	{ key: 'CCT', label: 'CCT' },
	{ key: 'hasCertisep', label: 'Conexión con ALUMNIX' }
] as const

const getInstitutionField = (key: (typeof statusFields)[number]['key']) => selectedInstitution.value?.[key]
</script>

<template>
	<div class="plan-estudios">
		<VaCard class="bitacora-card">
			<VaCardTitle class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="menu_book" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Plan de estudios</h2>
							<p class="header-subtitle">Carga el Excel institucional, revisa los datos generados y actualiza la estructura en el sistema.</p>
						</div>
						<va-button class="action-button action-button--save" :loading="submitting" :disabled="submitting || processingExcel" @click="saveNewChanges()">
							<va-icon name="save" class="button-icon" />
							Guardar cambios
						</va-button>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="card-content">
				<div class="pe-toolbar">
					<div class="pe-toolbar__meta">
						<span class="pe-chip pe-chip--accent">ID: {{ selectedInstitution?.idNombreInstitucion || 'Sin dato' }}</span>
						<p class="pe-hint">Procesa el archivo Excel para generar la estructura y compárala con los datos actuales antes de guardar.</p>
					</div>
				</div>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="tune" class="pe-section__icon" />
						<h3 class="pe-section__title">Configuración de carga</h3>
					</div>
					<div class="pe-settings-grid">
						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="table_chart" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Archivo Excel</h4>
									<p class="pe-panel__desc">Carga el Excel de la institución para generar la estructura.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaFileUpload
									v-model="excelFile"
									uploadButtonText="Cargar excel de la institución"
									color="success"
									type="single"
									fileTypes=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
									:disabled="submitting || processingExcel"
									class="upload-button" />
								<div v-if="processingExcel" class="processing-indicator">
									<va-icon name="hourglass_empty" spin class="processing-icon" />
									<span class="processing-text">Procesando Excel…</span>
								</div>
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="text_fields" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Configuración de texto</h4>
									<p class="pe-panel__desc">Afecta nombre y CURP de alumno y responsable.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="convertToUpperCase"
									:options="[
										{ text: 'Convertir textos a mayúsculas', value: true },
										{ text: 'Mantener textos en minúsculas', value: false }
									]"
									value-by="value" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="image" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Logo de la institución</h4>
									<p class="pe-panel__desc">Imagen cuadrada redimensionada a 128×128 px.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaBadge text="128×128 px" color="secondary" class="upload-badge" />
								<VaFileUpload
									v-model="logo128"
									undo
									type="single"
									fileTypes=".png,.jpg,.jpeg,.gif,.bmp,.webp"
									:disabled="submitting || processingExcel"
									uploadButtonText="Cargar logo"
									class="upload-button" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="phonelink_lock" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Firma desde app móvil</h4>
									<p class="pe-panel__desc">Habilita o deshabilita la firma in-app para la institución.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="inAppSign"
									:options="[
										{ text: 'Habilitar', value: true },
										{ text: 'Deshabilitar', value: false }
									]"
									value-by="value" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="badge" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Clave CCT</h4>
									<p class="pe-panel__desc">Clave del centro de trabajo de la institución.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaInput v-model="CCT" placeholder="Ingresa la clave CCT" />
							</div>
						</article>

						<article class="pe-panel">
							<div class="pe-panel__head">
								<va-icon name="hub" class="pe-panel__icon" />
								<div>
									<h4 class="pe-panel__title">Conexión con ALUMNIX</h4>
									<p class="pe-panel__desc">Permite la integración con la plataforma Alumnix.</p>
								</div>
							</div>
							<div class="pe-panel__body">
								<VaRadio
									v-model="hasCertisep"
									:options="[
										{ text: 'Habilitar', value: true },
										{ text: 'Deshabilitar', value: false }
									]"
									value-by="value" />
							</div>
						</article>
					</div>
				</section>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="compare_arrows" class="pe-section__icon" />
						<h3 class="pe-section__title">Comparación de datos</h3>
					</div>
					<div class="pe-compare">
						<div class="pe-compare__col_right">
							<div class="pe-json-header">
								<div class="pe-json-header__text">
									<span class="pe-json-header__label">Datos a cargar</span>
									<VaBadge :text="`ID: ${institutionData?.idNombreInstitucion || 'Sin dato'}`" color="success" size="small" />
								</div>
								<va-button v-if="institutionData" preset="secondary" size="small" class="pe-copy-btn" @click="copyToClipboard(institutionData)">
									<va-icon name="content_copy" />
									Copiar
								</va-button>
							</div>
							<div class="json-viewer" v-html="formatJsonWithColors(institutionData)"></div>
						</div>

						<div class="pe-compare__col">
							<div class="pe-json-header">
								<div class="pe-json-header__text">
									<span class="pe-json-header__label">Datos actuales</span>
									<VaBadge :text="`ID: ${selectedInstitution?.idNombreInstitucion || 'Sin dato'}`" color="info" size="small" />
								</div>
								<va-button v-if="selectedInstitution" preset="secondary" size="small" class="pe-copy-btn" @click="copyToClipboard(selectedInstitution)">
									<va-icon name="content_copy" />
									Copiar
								</va-button>
							</div>
							<div class="json-viewer" v-html="formatJsonWithColors(selectedInstitution)"></div>

							<div class="institution-status-section">
								<div class="status-section-header">
									<va-icon name="fact_check" class="status-section-icon" />
									<h4 class="status-section-title">Estado de datos de la institución</h4>
								</div>
								<div class="status-grid">
									<div
										v-for="field in statusFields"
										:key="field.key"
										class="status-item"
										:class="{
											'status-available': tieneValor(getInstitutionField(field.key)),
											'status-missing': !tieneValor(getInstitutionField(field.key))
										}">
										<va-icon :name="tieneValor(getInstitutionField(field.key)) ? 'check_circle' : 'cancel'" class="status-icon" />
										<div class="status-content">
											<span class="status-label">{{ field.label }}</span>
											<VaBadge
												:text="tieneValor(getInstitutionField(field.key)) ? 'Disponible' : 'No disponible'"
												:color="tieneValor(getInstitutionField(field.key)) ? 'success' : 'danger'"
												size="small" />
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</VaCardContent>
		</VaCard>

		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
			<template #body>
				<div class="success-message">
					<div class="success-header">
						<div class="success-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="success-title">Éxito</h3>
					</div>
					<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.plan-estudios {
	--pe-navy: #0f3569;
	--pe-navy-deep: #062342;
	--pe-accent: #24c8ff;
	--pe-accent-2: #169bff;
	--pe-border: rgba(15, 53, 105, 0.12);
	--pe-surface: #ffffff;
	--pe-canvas: #f4f7fb;
	--pe-text: #0f3569;
	--pe-text-2: rgba(15, 53, 105, 0.72);
	--pe-radius: 16px;
	--pe-radius-sm: 12px;
	--pe-shadow: 0 8px 24px rgba(15, 53, 105, 0.08);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
	box-sizing: border-box;
}

.bitacora-card {
	border-radius: var(--pe-radius);
	box-shadow: var(--pe-shadow);
	animation: fadeInUp 0.5s ease-out;
	overflow: hidden;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.header-gradient {
	display: block;
	width: 100%;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 1.25rem 1.5rem;
	border-radius: 0;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.header-gradient::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
	width: 100%;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	flex-wrap: wrap;
	width: 100%;
}

.header-icon-wrapper {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18);
}

.header-icon {
	color: white;
	font-size: 1.75rem;
}

.header-text {
	flex: 1;
	min-width: 200px;
}

.header-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0;
	letter-spacing: -0.02em;
	text-transform: none;
	line-height: 1.2;
}

.header-subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.78);
	line-height: 1.45;
	max-width: 52ch;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.action-button--save {
	margin-left: auto;
	min-width: 190px;
	background: linear-gradient(90deg, var(--pe-accent-2), var(--pe-accent)) !important;
	border: none !important;
	font-weight: 600;
}

.card-content {
	padding: 1.5rem;
	background: var(--pe-canvas);
	overflow-x: hidden;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
	min-width: 0;
}

.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #e8eef5;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #9eb3cc;
	border-radius: 4px;
}

.pe-toolbar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid var(--pe-border);
}

.pe-toolbar__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
}

.pe-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.3rem 0.75rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--pe-text);
	background: rgba(15, 53, 105, 0.06);
	border: 1px solid var(--pe-border);
}

.pe-chip--accent {
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.12);
	border-color: rgba(36, 200, 255, 0.28);
}

.pe-hint {
	flex-basis: 100%;
	margin: 0.35rem 0 0;
	font-size: 0.875rem;
	color: var(--pe-text-2);
	line-height: 1.5;
}

.pe-section + .pe-section {
	margin-top: 1.5rem;
	padding-top: 1.5rem;
	border-top: 1px solid var(--pe-border);
}

.pe-section__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
}

.pe-section__icon {
	color: var(--pe-navy-deep);
	font-size: 1.25rem;
}

.pe-section__title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: var(--pe-text);
	letter-spacing: -0.01em;
}

.pe-settings-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
	gap: 0.875rem;
	width: 100%;
	min-width: 0;
}

.pe-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 1rem 1.1rem;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.pe-panel:hover {
	border-color: rgba(36, 200, 255, 0.35);
	box-shadow: 0 4px 14px rgba(15, 53, 105, 0.06);
}

.pe-panel__head {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid rgba(15, 53, 105, 0.08);
}

.pe-panel__icon {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 10px;
	font-size: 1.1rem;
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.1);
}

.pe-panel__title {
	margin: 0;
	font-size: 0.95rem;
	font-weight: 700;
	color: var(--pe-text);
}

.pe-panel__desc {
	margin: 0.2rem 0 0;
	font-size: 0.78rem;
	color: var(--pe-text-2);
	line-height: 1.4;
}

.pe-panel__body {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-width: 0;
	overflow-wrap: anywhere;
}

.pe-panel__body :deep(.va-radio) {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.5rem;
	width: 100%;
}

.pe-panel__body :deep(.va-radio__container),
.pe-panel__body :deep(.va-radio-option),
.pe-panel__body :deep(.va-radio__option) {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.45rem;
	width: 100%;
}

.pe-panel__body :deep(.va-radio__text),
.pe-panel__body :deep(.va-radio-option__label),
.pe-panel__body :deep(.va-radio__label) {
	white-space: normal;
	overflow-wrap: anywhere;
	line-height: 1.35;
}

.pe-compare {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1rem;
	align-items: start;
	width: 100%;
	min-width: 0;
}

.pe-compare__col,
.pe-compare__col_right {
	min-width: 0;
	max-width: 100%;
}
.pe-json-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin-bottom: 0.65rem;
}

.pe-json-header__text {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.pe-json-header__label {
	font-size: 0.875rem;
	font-weight: 700;
	color: var(--pe-text);
}

.pe-copy-btn {
	flex-shrink: 0;
}

.upload-badge {
	align-self: flex-start;
}

.upload-button {
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.upload-button :deep(.va-file-upload),
.upload-button :deep(.va-file-upload__field),
.upload-button :deep(.va-button) {
	max-width: 100%;
}

.upload-button :deep(.va-button__content) {
	white-space: normal;
	text-align: center;
	line-height: 1.3;
}

.processing-indicator {
	display: flex;
	align-items: center;
	gap: 0.65rem;
	padding: 0.75rem 0.9rem;
	background: rgba(36, 200, 255, 0.08);
	border-radius: 10px;
	border-left: 3px solid var(--pe-navy-deep);
}

.processing-icon {
	color: var(--pe-navy-deep);
	font-size: 1.1rem;
}

.processing-text {
	color: var(--pe-navy-deep);
	font-weight: 600;
	font-size: 0.875rem;
}

.json-viewer {
	padding: 1rem;
	background: #1a1f2e;
	border-radius: 10px;
	font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
	font-size: 0.8rem;
	line-height: 1.55;
	color: #e8e8e8;
	overflow: auto;
	max-width: 100%;
	max-height: 420px;
	border: 1px solid rgba(15, 53, 105, 0.25);
	white-space: pre;
}

.json-viewer::-webkit-scrollbar {
	width: 8px;
	height: 8px;
}

.json-viewer::-webkit-scrollbar-track {
	background: #252b3d;
	border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb {
	background: #4a5568;
	border-radius: 4px;
}

.json-viewer :deep(.json-string) {
	color: #ce9178;
}

.json-viewer :deep(.json-number) {
	color: #b5cea8;
}

.json-viewer :deep(.json-boolean) {
	color: #569cd6;
	font-weight: 600;
}

.json-viewer :deep(.json-null) {
	color: #808080;
	font-style: italic;
}

.json-viewer :deep(.json-brace),
.json-viewer :deep(.json-bracket),
.json-viewer :deep(.json-colon),
.json-viewer :deep(.json-comma) {
	color: #d4d4d4;
}

.json-viewer :deep(.json-brace),
.json-viewer :deep(.json-bracket) {
	font-weight: 600;
}

.json-viewer :deep(.json-colon) {
	margin: 0 2px;
}

.institution-status-section {
	margin-top: 1rem;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 1rem;
}

.status-section-header {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 0.85rem;
	padding-bottom: 0.75rem;
	border-bottom: 1px solid rgba(15, 53, 105, 0.08);
}

.status-section-icon {
	color: var(--pe-navy-deep);
	font-size: 1.15rem;
}

.status-section-title {
	font-size: 0.95rem;
	font-weight: 700;
	color: var(--pe-text);
	margin: 0;
}

.status-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 0.65rem;
}

.status-item {
	display: flex;
	align-items: center;
	gap: 0.65rem;
	padding: 0.7rem 0.8rem;
	background: rgba(15, 53, 105, 0.03);
	border-radius: 10px;
	border: 1px solid rgba(15, 53, 105, 0.08);
	border-left-width: 3px;
	transition: background 0.2s ease;
}

.status-item.status-available {
	background: rgba(25, 135, 84, 0.05);
	border-left-color: #198754;
}

.status-item.status-missing {
	background: rgba(220, 53, 69, 0.05);
	border-left-color: #dc3545;
}

.status-icon {
	font-size: 1.25rem;
	flex-shrink: 0;
}

.status-item.status-available .status-icon {
	color: #198754;
}

.status-item.status-missing .status-icon {
	color: #dc3545;
}

.status-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	min-width: 0;
}

.status-label {
	font-size: 0.8rem;
	font-weight: 600;
	color: var(--pe-text-2);
}

.button-icon {
	margin-right: 0.35rem;
}

.error-description,
.success-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
}

.error-description {
	border-left: 4px solid #f71d1d;
}

.success-description {
	border-left: 4px solid #28a745;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 1px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(40, 167, 69, 0.2);
	animation: fadeInUp 0.35s ease-out;
}

.error-header,
.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon,
.success-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.error-icon {
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.success-icon {
	background: #1e7e34;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.35rem;
	font-weight: 700;
}

:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 1200px) {
	.pe-compare {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 900px) {
	.pe-settings-grid {
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1rem;
	}

	.header-main {
		flex-direction: column;
		align-items: stretch;
	}

	.action-button--save {
		margin-left: 0;
		width: 100%;
	}

	.pe-settings-grid,
	.status-grid {
		grid-template-columns: 1fr;
	}

	.pe-json-header {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
